import { useCallback, useEffect, useMemo, useState } from 'react'
import { useActiveOption } from './useActiveOption'
import { useAppSelector } from '@/store/store-hooks'
import { selectActiveMatter } from '@/matter/store/selectors'
import { selectActiveMatterExternalSyncMapping } from '@/user_files/external-storage/store/slice'
import { getSourceRootLabel } from '../source-root'
import { searchMatterFiles } from '../fetch/search-matter-files'
import { searchMatterFolders } from '../fetch/search-matter-folders'
import { formatSuggestionPath } from '../suggestion-path'
import { kSuggestionsDebounceMs, kSuggestionsPageLimit, kSuggestionsPerSection } from '../constants'
import type { FileSearchResult, FolderSearchResult } from '../schemas'

export type SearchSuggestion =
  | { kind: 'file'; id: string; name: string; locationLabel: string; data: FileSearchResult }
  | { kind: 'folder'; id: string; name: string; locationLabel: string; data: FolderSearchResult }

export type SearchSuggestions = {
  /** File matches, capped at the per-section limit. Rendered above the folder section. */
  files: SearchSuggestion[]
  /** Folder matches, capped at the per-section limit. */
  folders: SearchSuggestion[]
  /** Files then folders, in render order — the list arrow keys walk. */
  items: SearchSuggestion[]
  /** True when the dropdown should be on screen, whether or not anything matched. */
  isOpen: boolean
  /**
   * Index into `items`, or -1 when nothing is active. `items.length` is the "All search results"
   * row after the suggestions, which commits the query rather than naming an item.
   */
  activeIndex: number
  setActiveIndex: (index: number) => void
  /** Moves the active row, wrapping at both ends. */
  moveActiveIndex: (delta: number) => void
  /**
   * Closes the dropdown until the query changes again. Pass the query being committed when it is
   * about to replace the field's contents, so the dismissal applies to that query rather than to
   * the one on screen when the click happened.
   */
  dismiss: (query?: string) => void
  /** Reopens a dismissed dropdown for the query still in the field. */
  open: () => void
}

type FetchedSuggestions = {
  files: FileSearchResult[]
  folders: FolderSearchResult[]
}

const EMPTY_FETCHED: FetchedSuggestions = { files: [], folders: [] }

/**
 * Typeahead suggestions for the matter files search field.
 *
 * This is the typing stage of search, separate from the committed results the table shows on Enter:
 * it asks for one small page per endpoint and renders the two responses as two independent
 * sections. Because the sections are independent, a half that fails simply does not render — unlike
 * the results table, which fails as a pair so it never shows half a result set.
 *
 * @param params.query - The live input value, debounced here rather than by the caller
 * @param params.isFocused - Whether the search field has focus; the dropdown closes when it does not
 */
export function useSearchSuggestions({ query, isFocused }: { query: string; isFocused: boolean }): SearchSuggestions {
  const activeMatter = useAppSelector(selectActiveMatter)
  const activeMatterId = activeMatter?.id ?? null
  const matterName = activeMatter?.name ?? 'Matter'
  // Read, never fetched here: the drive's source view already loads the mapping.
  const hasSyncedDrive = Boolean(useAppSelector((state) => selectActiveMatterExternalSyncMapping(state, activeMatter?.id)))

  const [fetched, setFetched] = useState<FetchedSuggestions>(EMPTY_FETCHED)
  // Which query the user dismissed, rather than a bare flag: committing a recent search writes a
  // new value into the field, and a flag keyed on nothing would be cleared by that very write —
  // reopening the dropdown on top of the results the commit just produced.
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null)

  const trimmedQuery = query.trim()

  // An empty field has no query, so it can hold no dismissal. Without this the value outlives the
  // field it belonged to, and retyping that same term reads as still-dismissed — which is what
  // happens after picking a suggestion, since that clears the field.
  useEffect(() => {
    if (!trimmedQuery) setDismissedQuery(null)
  }, [trimmedQuery])

  const dismissed = dismissedQuery !== null && dismissedQuery === trimmedQuery

  useEffect(() => {
    if (!activeMatterId || !trimmedQuery) {
      setFetched(EMPTY_FETCHED)
      return
    }

    // Committing the search or pressing Escape dismisses the list; the cleanup below then cancels
    // the request it would have made, so a commit does not also pay for suggestions nobody sees.
    if (dismissed) return

    const controller = new AbortController()
    let cancelled = false

    // Wait for a pause in typing, then fire once. The previous request is aborted by this effect's
    // cleanup, so only the newest query can write results.
    const timer = setTimeout(() => {
      const pageOptions = { limit: kSuggestionsPageLimit, maxPages: 1, signal: controller.signal }

      Promise.allSettled([
        searchMatterFolders(activeMatterId, { query: trimmedQuery, ...pageOptions }),
        searchMatterFiles(activeMatterId, { query: trimmedQuery, ...pageOptions }),
      ]).then(([folderOutcome, fileOutcome]) => {
        if (cancelled) return

        // A rejected half drops its section rather than emptying the dropdown. The fetchers already
        // report to Sentry, and committing the search on Enter surfaces a real error in the table.
        setFetched({
          folders: folderOutcome.status === 'fulfilled' ? folderOutcome.value.folders : [],
          files: fileOutcome.status === 'fulfilled' ? fileOutcome.value.files : [],
        })
      })
    }, kSuggestionsDebounceMs)

    return () => {
      cancelled = true
      clearTimeout(timer)
      controller.abort()
    }
  }, [activeMatterId, trimmedQuery, dismissed])

  const { files, folders, items } = useMemo(() => {
    // Suggestions span both sources for the same reason the results table does — see the note in
    // `useMatterFilesSearch`. Nothing is filtered by storage origin here.
    const fileSuggestions: SearchSuggestion[] = fetched.files.slice(0, kSuggestionsPerSection).map((file) => ({
      kind: 'file' as const,
      id: file.id,
      name: file.original_filename,
      locationLabel: formatSuggestionPath(file.folder_path, matterName, getSourceRootLabel(file, hasSyncedDrive)),
      data: file,
    }))

    const folderSuggestions: SearchSuggestion[] = fetched.folders.slice(0, kSuggestionsPerSection).map((folder) => ({
      kind: 'folder' as const,
      id: folder.id,
      name: folder.name,
      locationLabel: formatSuggestionPath(folder.folder_path, matterName, getSourceRootLabel(folder, hasSyncedDrive)),
      data: folder,
    }))

    return { files: fileSuggestions, folders: folderSuggestions, items: [...fileSuggestions, ...folderSuggestions] }
  }, [fetched.files, fetched.folders, matterName, hasSyncedDrive])

  // Keyed on the query rather than on the response: reopening the list refetches, and resetting on
  // the new rows' identity would drop the user's selection mid-keystroke.
  const { activeIndex, setActiveIndex, moveActiveIndex } = useActiveOption(items.length + 1, trimmedQuery)

  // Open even with no matches: the "All search results" row is what tells the user Enter still
  // runs a full search, and it matters most when there is nothing else to show.
  const isOpen = isFocused && !dismissed && trimmedQuery.length > 0

  const dismiss = useCallback((query?: string) => setDismissedQuery((query ?? trimmedQuery).trim()), [trimmedQuery])
  const open = useCallback(() => setDismissedQuery(null), [])

  return { files, folders, items, isOpen, activeIndex, setActiveIndex, moveActiveIndex, dismiss, open }
}
